import { assertNever } from "./assertNever";
import { isJust, just, nothing, type Maybe } from "./maybe";
import type { ZwChapter, ZwChapterNumbering } from "./zwConfig/types";

export type ChapterNumberingOverride = {
  counted: boolean;
  heading: "default" | "name";
};
export type ChapterHeading = {
  folder: string;
  number: Maybe<number>;
  lines: readonly string[];
  text: string;
  canRename: boolean;
  renamePrefix: string;
};

export const CHAPTER_PATTERNS = [
  { value: "numberName", label: "Number and name" },
  { value: "number", label: "Number only" },
  { value: "name", label: "Name only" },
  { value: "numberAbove", label: "Number above name" },
] as const;
export const CHAPTER_NUMBER_STYLES = [
  { value: "arabic", label: "1, 2, 3" },
  { value: "words", label: "One, Two, Three" },
  { value: "roman", label: "I, II, III" },
] as const;

export function defaultChapterNumbering(): ZwChapterNumbering {
  return { pattern: "name", style: "arabic", prefix: true };
}
/** Initial settings for new projects; legacy missing settings still use names. */
export function newProjectNumbering(
  kind: "Chapter" | "Act",
): ZwChapterNumbering {
  return {
    pattern: "numberName",
    style: kind === "Act" ? "roman" : "arabic",
    prefix: true,
  };
}
export function defaultChapterOverride(): ChapterNumberingOverride {
  return { counted: true, heading: "default" };
}
export type ChapterNamingMode = "default" | "custom" | "unnumbered";

/** Both legacy uncounted combinations mean an unnumbered, named section. */
export function chapterNamingMode(
  override: ChapterNumberingOverride,
): ChapterNamingMode {
  return override.counted === false
    ? "unnumbered"
    : override.heading === "name"
      ? "custom"
      : "default";
}

export function chapterOverrideForNaming(
  mode: ChapterNamingMode,
): ChapterNumberingOverride {
  switch (mode) {
    case "default":
      return defaultChapterOverride();
    case "custom":
      return { counted: true, heading: "name" };
    case "unnumbered":
      return { counted: false, heading: "name" };
    default:
      return assertNever(mode);
  }
}

export function normalizeChapterNumbering(raw: unknown): ZwChapterNumbering {
  const obj =
    raw !== null && typeof raw === "object"
      ? (raw as Record<string, unknown>)
      : {};
  return {
    pattern:
      obj.pattern === "numberName" ||
      obj.pattern === "number" ||
      obj.pattern === "numberAbove"
        ? obj.pattern
        : "name",
    style:
      obj.style === "words" || obj.style === "roman" ? obj.style : "arabic",
    prefix: obj.prefix !== false,
  };
}
export function normalizeChapterOverrides(
  raw: unknown,
): Record<string, ChapterNumberingOverride> {
  if (raw === null || typeof raw !== "object") return {};
  return Object.fromEntries(
    Object.entries(raw).map(([folder, value]) => {
      const obj =
        value !== null && typeof value === "object"
          ? (value as Record<string, unknown>)
          : {};
      return [
        folder,
        {
          counted: obj.counted !== false,
          heading: obj.heading === "name" ? "name" : "default",
        },
      ];
    }),
  );
}

const SMALL = [
  "Zero",
  "One",
  "Two",
  "Three",
  "Four",
  "Five",
  "Six",
  "Seven",
  "Eight",
  "Nine",
  "Ten",
  "Eleven",
  "Twelve",
  "Thirteen",
  "Fourteen",
  "Fifteen",
  "Sixteen",
  "Seventeen",
  "Eighteen",
  "Nineteen",
];
const TENS = [
  "",
  "",
  "Twenty",
  "Thirty",
  "Forty",
  "Fifty",
  "Sixty",
  "Seventy",
  "Eighty",
  "Ninety",
];
function words(n: number): string {
  if (n < 20) return SMALL[n] ?? String(n);
  if (n < 100)
    return `${TENS[Math.floor(n / 10)] ?? ""}${n % 10 === 0 ? "" : `-${words(n % 10)}`}`;
  for (const [size, label] of [
    [1_000_000_000, "Billion"],
    [1_000_000, "Million"],
    [1000, "Thousand"],
    [100, "Hundred"],
  ] as const) {
    if (n >= size)
      return `${words(Math.floor(n / size))} ${label}${n % size === 0 ? "" : ` ${words(n % size)}`}`;
  }
  return String(n);
}
const ROMAN_GLYPHS = [
  [1000, "M"],
  [900, "CM"],
  [500, "D"],
  [400, "CD"],
  [100, "C"],
  [90, "XC"],
  [50, "L"],
  [40, "XL"],
  [10, "X"],
  [9, "IX"],
  [5, "V"],
  [4, "IV"],
  [1, "I"],
] as const;

function roman(n: number): string {
  return ROMAN_GLYPHS.reduce(
    (state, [value, label]) => {
      const count = Math.floor(state.rest / value);
      return {
        rest: state.rest - count * value,
        text: state.text + label.repeat(count),
      };
    },
    { rest: n, text: "" },
  ).text;
}

function styledNumber(
  value: number,
  style: ZwChapterNumbering["style"],
): string {
  switch (style) {
    case "arabic":
      return String(value);
    case "words":
      return words(value);
    case "roman":
      return roman(value);
    default:
      return assertNever(style);
  }
}

function headingLines(
  name: string,
  number: Maybe<number>,
  settings: ZwChapterNumbering,
  kind: "Chapter" | "Act",
  nameOnly: boolean,
): { readonly lines: readonly string[]; readonly renamePrefix: string } {
  const untitled = name.length > 0 ? name : `Untitled ${kind.toLowerCase()}`;
  if (nameOnly || settings.pattern === "name" || isJust(number) === false) {
    return { lines: [untitled], renamePrefix: "" };
  }
  const value = styledNumber(number.value, settings.style);
  const label = settings.prefix ? `${kind} ${value}` : value;
  const renamePrefix =
    settings.pattern === "numberAbove" ? `${label}\n` : `${label}: `;
  switch (settings.pattern) {
    case "number":
      return { lines: [label], renamePrefix };
    case "numberName":
      return {
        lines: [name.length > 0 ? `${label}: ${name}` : label],
        renamePrefix,
      };
    case "numberAbove":
      return { lines: name.length > 0 ? [label, name] : [label], renamePrefix };
    default:
      return assertNever(settings.pattern);
  }
}

/** Legacy folders may be names; generated chapter IDs never become headings. */
export function storedChapterName(
  chapter: Pick<ZwChapter, "name" | "folder">,
): string {
  const name = chapter.name.trim();
  return name === chapter.folder &&
    /^(chapter|act)-[0-9a-f-]{32,36}$/i.test(name)
    ? ""
    : name;
}

/** Pass the complete, flattened manuscript before filtering an export subset. */
export function chapterHeadings(
  chapters: readonly Pick<ZwChapter, "name" | "folder">[],
  settings: ZwChapterNumbering,
  overrides: Readonly<Record<string, ChapterNumberingOverride>>,
  kind: "Chapter" | "Act" = "Chapter",
): ChapterHeading[] {
  const { headings } = chapters.reduce<{
    ordinal: number;
    headings: ChapterHeading[];
  }>(
    (state, chapter) => {
      const override = overrides[chapter.folder] ?? defaultChapterOverride();
      const counted = override.counted === true;
      const nextOrdinal = counted ? state.ordinal + 1 : state.ordinal;
      const number = counted ? just(nextOrdinal) : nothing;
      const name = storedChapterName(chapter);
      const built = headingLines(
        name,
        number,
        settings,
        kind,
        override.heading === "name",
      );
      const heading: ChapterHeading = {
        renamePrefix: built.renamePrefix,
        folder: chapter.folder,
        number,
        lines: built.lines,
        text: built.lines.join(": "),
        canRename:
          settings.pattern !== "number" ||
          override.heading === "name" ||
          override.counted === false,
      };
      return {
        ordinal: nextOrdinal,
        headings: [...state.headings, heading],
      };
    },
    { ordinal: 0, headings: [] },
  );
  return headings;
}

/** Scene positions follow the chapter’s current order, regardless of file names. */
export function chapterSceneLabel(
  chapter: ZwChapter,
  sceneFile: string,
  chapterLabel: string,
): string {
  const index = chapter.scenes.findIndex((scene) => scene.file === sceneFile);
  return index < 0 ? chapterLabel : `${chapterLabel}, scene ${index + 1}`;
}
